import { useState } from 'react'
import { Search, ChevronDown } from 'lucide-react'

const categoryOptions = [
  'Antibiotic',
  'Anti-inflammatory',
  'Analgesic',
  'Sedative',
  'Vaccine',
  'Parasiticide',
  'Hormone',
  'Other',
] as const

const controlledOptions = ['Yes', 'No'] as const

interface MedicationSearchProps {
  searchQuery: string
  onSearchChange: (query: string) => void
  categoryFilter: string
  onCategoryFilterChange: (category: string) => void
  controlledFilter: string
  onControlledFilterChange: (controlled: string) => void
}

export function MedicationSearch({
  searchQuery,
  onSearchChange,
  categoryFilter,
  onCategoryFilterChange,
  controlledFilter,
  onControlledFilterChange,
}: MedicationSearchProps) {
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false)
  const [controlledDropdownOpen, setControlledDropdownOpen] = useState(false)

  return (
    <div data-testid="medication-search" className="flex items-center gap-3 flex-wrap">
      {/* Search bar */}
      <div className="relative flex-1 min-w-[200px] max-w-[360px]">
        <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" />
        <input
          data-testid="medication-search-input"
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search by medication name..."
          className="w-full h-7 pl-7 pr-2 rounded-[4px] border border-border bg-base text-[12px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40 placeholder:text-text-disabled"
        />
      </div>

      {/* Category filter dropdown */}
      <div className="relative">
        <button
          data-testid="medication-category-filter"
          onClick={() => {
            setCategoryDropdownOpen(!categoryDropdownOpen)
            setControlledDropdownOpen(false)
          }}
          className="inline-flex items-center gap-1 h-7 px-2 rounded-[4px] border border-border bg-base text-[12px] text-text-primary hover:bg-hover transition-colors duration-100 cursor-pointer"
        >
          <span className="text-[11px] text-text-muted mr-0.5">Category:</span>
          {categoryFilter || 'All'}
          <ChevronDown size={12} className="text-text-muted" />
        </button>
        {categoryDropdownOpen && (
          <div
            data-testid="medication-category-filter-dropdown"
            className="absolute z-10 top-full left-0 mt-1 bg-surface border border-border rounded-[4px] min-w-[180px] max-h-48 overflow-y-auto"
            style={{ boxShadow: 'var(--shadow-elevation-1)' }}
          >
            <button
              type="button"
              data-testid="category-option-all"
              onClick={() => {
                onCategoryFilterChange('')
                setCategoryDropdownOpen(false)
              }}
              className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
            >
              All
            </button>
            {categoryOptions.map((cat) => (
              <button
                key={cat}
                type="button"
                data-testid={`category-option-${cat}`}
                onClick={() => {
                  onCategoryFilterChange(cat)
                  setCategoryDropdownOpen(false)
                }}
                className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
              >
                {cat}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Controlled status filter dropdown */}
      <div className="relative">
        <button
          data-testid="medication-controlled-filter"
          onClick={() => {
            setControlledDropdownOpen(!controlledDropdownOpen)
            setCategoryDropdownOpen(false)
          }}
          className="inline-flex items-center gap-1 h-7 px-2 rounded-[4px] border border-border bg-base text-[12px] text-text-primary hover:bg-hover transition-colors duration-100 cursor-pointer"
        >
          <span className="text-[11px] text-text-muted mr-0.5">Controlled:</span>
          {controlledFilter || 'All'}
          <ChevronDown size={12} className="text-text-muted" />
        </button>
        {controlledDropdownOpen && (
          <div
            data-testid="medication-controlled-filter-dropdown"
            className="absolute z-10 top-full left-0 mt-1 bg-surface border border-border rounded-[4px] min-w-[120px] max-h-40 overflow-y-auto"
            style={{ boxShadow: 'var(--shadow-elevation-1)' }}
          >
            <button
              type="button"
              data-testid="controlled-option-all"
              onClick={() => {
                onControlledFilterChange('')
                setControlledDropdownOpen(false)
              }}
              className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
            >
              All
            </button>
            {controlledOptions.map((opt) => (
              <button
                key={opt}
                type="button"
                data-testid={`controlled-option-${opt}`}
                onClick={() => {
                  onControlledFilterChange(opt)
                  setControlledDropdownOpen(false)
                }}
                className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
              >
                {opt}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
